// src/hooks/useDailyPlans.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { formatLocalDate, getWeekDatesLocal, addDaysLocal } from '@/lib/dateUtils';

// =====================================================
// TIMEFRAMES QUERIES & MUTATIONS
// =====================================================

/**
 * Get timeframes for a group, ordered by sequence
 */
export const useTimeframes = (groupId, options = {}) => {
  return useQuery({
    queryKey: ['daily_plan_timeframes', groupId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('daily_plan_timeframes')
        .select('*')
        .eq('group_id', groupId)
        .eq('is_active', true)
        .order('sequence_order', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!groupId,
    ...options
  });
};

/**
 * Create a new timeframe
 */
export const useCreateTimeframe = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (timeframeData) => {
      const { data, error } = await supabase
        .from('daily_plan_timeframes')
        .insert(timeframeData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['daily_plan_timeframes', variables.group_id]
      });
      toast.success('Przedział czasowy utworzony');
    },
    onError: (error) => {
      logger.error('Create timeframe error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Update a timeframe
 */
export const useUpdateTimeframe = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }) => {
      const { data, error } = await supabase
        .from('daily_plan_timeframes')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['daily_plan_timeframes', data.group_id]
      });
      toast.success('Przedział czasowy zaktualizowany');
    },
    onError: (error) => {
      logger.error('Update timeframe error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Delete a timeframe (soft delete)
 */
export const useDeleteTimeframe = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, groupId }) => {
      const { error } = await supabase
        .from('daily_plan_timeframes')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;
      return { groupId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: ['daily_plan_timeframes', result.groupId]
      });
      toast.success('Przedział czasowy usunięty');
    },
    onError: (error) => {
      logger.error('Delete timeframe error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Reorder timeframes
 */
export const useReorderTimeframes = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, orderedIds }) => {
      // Update sequence_order for each timeframe
      const updates = orderedIds.map((id, index) =>
        supabase
          .from('daily_plan_timeframes')
          .update({ sequence_order: index })
          .eq('id', id)
      );

      await Promise.all(updates);
      return { groupId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: ['daily_plan_timeframes', result.groupId]
      });
    },
    onError: (error) => {
      logger.error('Reorder timeframes error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

// =====================================================
// ACTIVITIES QUERIES & MUTATIONS
// =====================================================

/**
 * Get activities for a group
 */
export const useActivities = (groupId, options = {}) => {
  return useQuery({
    queryKey: ['daily_plan_activities', groupId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('daily_plan_activities')
        .select('*')
        .eq('group_id', groupId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data || [];
    },
    enabled: !!groupId,
    ...options
  });
};

/**
 * Create a new activity
 */
export const useCreateActivity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (activityData) => {
      const { data, error } = await supabase
        .from('daily_plan_activities')
        .insert(activityData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['daily_plan_activities', variables.group_id]
      });
      toast.success('Aktywność utworzona');
    },
    onError: (error) => {
      logger.error('Create activity error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Update an activity
 */
export const useUpdateActivity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }) => {
      const { data, error } = await supabase
        .from('daily_plan_activities')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['daily_plan_activities', data.group_id]
      });
      toast.success('Aktywność zaktualizowana');
    },
    onError: (error) => {
      logger.error('Update activity error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Delete an activity (soft delete)
 */
export const useDeleteActivity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, groupId }) => {
      const { error } = await supabase
        .from('daily_plan_activities')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;
      return { groupId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: ['daily_plan_activities', result.groupId]
      });
      toast.success('Aktywność usunięta');
    },
    onError: (error) => {
      logger.error('Delete activity error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

// =====================================================
// DAILY PLANS QUERIES & MUTATIONS
// =====================================================

/**
 * Get daily plan for a specific group and date
 */
export const useDailyPlan = (groupId, date, options = {}) => {
  return useQuery({
    queryKey: ['daily_plans', groupId, date],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('daily_plans')
        .select(`
          *,
          items:daily_plan_items(
            *,
            timeframe:daily_plan_timeframes(*),
            activity:daily_plan_activities(*)
          )
        `)
        .eq('group_id', groupId)
        .eq('plan_date', date)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!groupId && !!date,
    ...options
  });
};

/**
 * Get weekly plans for a group (Mon-Fri)
 */
export const useWeeklyPlans = (groupId, weekStartDate, options = {}) => {
  return useQuery({
    queryKey: ['daily_plans', 'week', groupId, weekStartDate],
    queryFn: async () => {
      // Get 5 days from weekStart (Mon-Fri) using centralized date util
      const dates = getWeekDatesLocal(weekStartDate);

      const { data, error } = await supabase
        .from('daily_plans')
        .select(`
          *,
          items:daily_plan_items(
            *,
            timeframe:daily_plan_timeframes(*),
            activity:daily_plan_activities(*)
          )
        `)
        .eq('group_id', groupId)
        .gte('plan_date', dates[0])
        .lte('plan_date', dates[dates.length - 1])
        .order('plan_date');

      if (error) throw error;
      return data || [];
    },
    enabled: !!groupId && !!weekStartDate,
    ...options
  });
};

/**
 * Create or get a daily plan for a date
 */
export const useGetOrCreateDailyPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, date, userId }) => {
      // First try to get existing
      const { data: existing, error: fetchError } = await supabase
        .from('daily_plans')
        .select('*')
        .eq('group_id', groupId)
        .eq('plan_date', date)
        .maybeSingle();

      if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;
      if (existing) return existing;

      // Create new
      const { data, error } = await supabase
        .from('daily_plans')
        .insert({
          group_id: groupId,
          plan_date: date,
          created_by: userId
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily_plans'] });
    },
    onError: (error) => {
      logger.error('Get or create daily plan error', { message: error.message });
    }
  });
};

/**
 * Update a daily plan
 */
export const useUpdateDailyPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }) => {
      const { data, error } = await supabase
        .from('daily_plans')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily_plans'] });
    },
    onError: (error) => {
      logger.error('Update daily plan error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Publish or unpublish a daily plan
 */
export const usePublishDailyPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ planId, publish }) => {
      const { data, error } = await supabase
        .from('daily_plans')
        .update({
          is_published: publish,
          published_at: publish ? new Date().toISOString() : null
        })
        .eq('id', planId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['daily_plans'] });
      if (data.is_published) {
        toast.success('Plan opublikowany - rodzice mogą go teraz zobaczyć');
      } else {
        toast.success('Plan wycofany z publikacji');
      }
    },
    onError: (error) => {
      logger.error('Publish daily plan error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

// =====================================================
// DAILY PLAN ITEMS MUTATIONS
// =====================================================

/**
 * Add a plan item (add activity to timeframe+date)
 * Automatically creates daily_plan if it doesn't exist
 * Supports multiple activities per timeframe
 */
export const useSetPlanItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, date, timeframeId, activityId, customName }) => {
      // First, get or create the daily plan for this date
      let dailyPlanId;

      // Try to get existing plan
      const { data: existingPlan, error: fetchError } = await supabase
        .from('daily_plans')
        .select('id')
        .eq('group_id', groupId)
        .eq('plan_date', date)
        .maybeSingle();

      if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;

      if (existingPlan) {
        dailyPlanId = existingPlan.id;
      } else {
        // Create new daily plan
        const { data: newPlan, error: createError } = await supabase
          .from('daily_plans')
          .insert({
            group_id: groupId,
            plan_date: date
          })
          .select('id')
          .single();

        if (createError) throw createError;
        dailyPlanId = newPlan.id;
      }

      // Get the next sequence_order for this timeframe
      const { data: existingItems, error: itemsError } = await supabase
        .from('daily_plan_items')
        .select('sequence_order')
        .eq('daily_plan_id', dailyPlanId)
        .eq('timeframe_id', timeframeId)
        .order('sequence_order', { ascending: false })
        .limit(1);

      if (itemsError) throw itemsError;

      const nextSequenceOrder = existingItems && existingItems.length > 0
        ? existingItems[0].sequence_order + 1
        : 0;

      // Insert new plan item
      const { data, error } = await supabase
        .from('daily_plan_items')
        .insert({
          daily_plan_id: dailyPlanId,
          timeframe_id: timeframeId,
          activity_id: activityId,
          custom_activity_name: customName || null,
          sequence_order: nextSequenceOrder
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily_plans'] });
    },
    onError: (error) => {
      logger.error('Add plan item error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Clear a plan item (remove activity from timeframe)
 */
export const useClearPlanItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId }) => {
      const { error } = await supabase
        .from('daily_plan_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily_plans'] });
    },
    onError: (error) => {
      logger.error('Clear plan item error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

// =====================================================
// TEMPLATES QUERIES & MUTATIONS
// =====================================================

/**
 * Get templates for a group
 */
export const usePlanTemplates = (groupId, options = {}) => {
  return useQuery({
    queryKey: ['daily_plan_templates', groupId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('daily_plan_templates')
        .select('*')
        .eq('group_id', groupId)
        .order('name');

      if (error) throw error;
      return data || [];
    },
    enabled: !!groupId,
    ...options
  });
};

/**
 * Create a plan template
 */
export const useCreatePlanTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateData) => {
      const { data, error } = await supabase
        .from('daily_plan_templates')
        .insert(templateData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['daily_plan_templates', variables.group_id]
      });
      toast.success('Szablon zapisany');
    },
    onError: (error) => {
      logger.error('Create template error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Delete a template
 */
export const useDeletePlanTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, groupId }) => {
      const { error } = await supabase
        .from('daily_plan_templates')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { groupId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: ['daily_plan_templates', result.groupId]
      });
      toast.success('Szablon usunięty');
    },
    onError: (error) => {
      logger.error('Delete template error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

// =====================================================
// COPY/PASTE OPERATIONS
// =====================================================

/**
 * Copy a day to another date
 */
export const useCopyDayToDate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sourcePlanId, targetGroupId, targetDate, userId }) => {
      // Get source plan items
      const { data: sourceItems, error: fetchError } = await supabase
        .from('daily_plan_items')
        .select('*')
        .eq('daily_plan_id', sourcePlanId);

      if (fetchError) throw fetchError;

      // Create or get target plan
      const { data: targetPlan, error: upsertError } = await supabase
        .from('daily_plans')
        .upsert({
          group_id: targetGroupId,
          plan_date: targetDate,
          created_by: userId
        }, { onConflict: 'group_id,plan_date' })
        .select()
        .single();

      if (upsertError) throw upsertError;

      // Delete existing items on target
      await supabase
        .from('daily_plan_items')
        .delete()
        .eq('daily_plan_id', targetPlan.id);

      // Copy items to target
      if (sourceItems && sourceItems.length > 0) {
        const newItems = sourceItems.map(item => ({
          daily_plan_id: targetPlan.id,
          timeframe_id: item.timeframe_id,
          activity_id: item.activity_id,
          custom_activity_name: item.custom_activity_name,
          custom_description: item.custom_description,
          sequence_order: item.sequence_order
        }));

        const { error: insertError } = await supabase
          .from('daily_plan_items')
          .insert(newItems);

        if (insertError) throw insertError;
      }

      return targetPlan;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily_plans'] });
      toast.success('Dzień skopiowany');
    },
    onError: (error) => {
      logger.error('Copy day error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Copy entire week to next week
 */
export const useCopyWeekToNext = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, sourceWeekStart, userId }) => {
      // Calculate target week start (7 days later)
      const targetDate = addDaysLocal(sourceWeekStart, 7);

      // Get source week plans using centralized date util
      const sourceDates = getWeekDatesLocal(sourceWeekStart);

      const { data: sourcePlans, error: fetchError } = await supabase
        .from('daily_plans')
        .select(`
          *,
          items:daily_plan_items(*)
        `)
        .eq('group_id', groupId)
        .in('plan_date', sourceDates);

      if (fetchError) throw fetchError;

      // Copy each day
      for (const sourcePlan of sourcePlans || []) {
        const sourceIdx = sourceDates.indexOf(sourcePlan.plan_date);
        const targetDateStr = formatLocalDate(addDaysLocal(targetDate, sourceIdx));

        // Create target plan
        const { data: newPlan, error: createError } = await supabase
          .from('daily_plans')
          .upsert({
            group_id: groupId,
            plan_date: targetDateStr,
            created_by: userId
          }, { onConflict: 'group_id,plan_date' })
          .select()
          .single();

        if (createError) throw createError;

        // Delete existing items
        await supabase
          .from('daily_plan_items')
          .delete()
          .eq('daily_plan_id', newPlan.id);

        // Copy items
        if (sourcePlan.items && sourcePlan.items.length > 0) {
          const newItems = sourcePlan.items.map(item => ({
            daily_plan_id: newPlan.id,
            timeframe_id: item.timeframe_id,
            activity_id: item.activity_id,
            custom_activity_name: item.custom_activity_name,
            custom_description: item.custom_description,
            sequence_order: item.sequence_order
          }));

          await supabase.from('daily_plan_items').insert(newItems);
        }
      }

      return { targetWeekStart: formatLocalDate(targetDate) };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily_plans'] });
      toast.success('Tydzień skopiowany do następnego');
    },
    onError: (error) => {
      logger.error('Copy week error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Apply a template to a week
 */
export const useApplyTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ templateId, groupId, weekStartDate, userId }) => {
      // Get template
      const { data: template, error: fetchError } = await supabase
        .from('daily_plan_templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (fetchError) throw fetchError;

      const templateData = template.template_data;
      const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
      const weekDates = getWeekDatesLocal(weekStartDate);

      for (let i = 0; i < 5; i++) {
        const dayKey = dayKeys[i];
        const dayData = templateData[dayKey];
        if (!dayData || dayData.length === 0) continue;

        const planDateStr = weekDates[i];

        // Create or get plan for this day
        const { data: plan, error: planError } = await supabase
          .from('daily_plans')
          .upsert({
            group_id: groupId,
            plan_date: planDateStr,
            created_by: userId
          }, { onConflict: 'group_id,plan_date' })
          .select()
          .single();

        if (planError) throw planError;

        // Delete existing items
        await supabase
          .from('daily_plan_items')
          .delete()
          .eq('daily_plan_id', plan.id);

        // Insert template items
        const items = dayData.map((item, idx) => ({
          daily_plan_id: plan.id,
          timeframe_id: item.timeframe_id,
          activity_id: item.activity_id || null,
          custom_activity_name: item.custom_name || null,
          sequence_order: idx
        }));

        if (items.length > 0) {
          await supabase.from('daily_plan_items').insert(items);
        }
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily_plans'] });
      toast.success('Szablon zastosowany');
    },
    onError: (error) => {
      logger.error('Apply template error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};

/**
 * Save current week as a template
 */
export const useSaveWeekAsTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, weekStartDate, templateName, userId }) => {
      // Get week plans using centralized date util
      const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
      const dates = getWeekDatesLocal(weekStartDate);

      const { data: plans, error: fetchError } = await supabase
        .from('daily_plans')
        .select(`
          *,
          items:daily_plan_items(*)
        `)
        .eq('group_id', groupId)
        .in('plan_date', dates);

      if (fetchError) throw fetchError;

      // Build template data
      const templateData = {};
      for (let i = 0; i < 5; i++) {
        const date = dates[i];
        const plan = plans?.find(p => p.plan_date === date);

        templateData[dayKeys[i]] = plan?.items?.map(item => ({
          timeframe_id: item.timeframe_id,
          activity_id: item.activity_id,
          custom_name: item.custom_activity_name
        })) || [];
      }

      // Create template
      const { data, error } = await supabase
        .from('daily_plan_templates')
        .insert({
          group_id: groupId,
          name: templateName,
          template_data: templateData,
          created_by: userId
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['daily_plan_templates', variables.groupId]
      });
      toast.success('Szablon zapisany');
    },
    onError: (error) => {
      logger.error('Save template error', { message: error.message });
      toast.error(`Błąd: ${error.message}`);
    }
  });
};
